import { useCallback } from 'react';
import type {
  JobPhaseAssignConflictDto,
  JobPhaseAssignContractorDto,
  JobPhaseDto,
  JobPhasePreviousAssignmentDto,
} from '@vatos-pas/common';
import { axiosInstance } from 'services/http';
import { extractErrorMessage } from 'utils';

export type AssignContractorResult =
  | { status: 'assigned'; jobPhase: JobPhaseDto }
  | {
      status: 'previously-assigned';
      previouslyAssignedPhases: JobPhasePreviousAssignmentDto[];
    };

/**
 * Regional Manager assignment through POST job-phase/:id/assign-contractor.
 *
 * One request replaces the old GET job-phase (whole job) → GET job-phase/:id →
 * PATCH chain: the API answers 409 with the phases this contractor already
 * worked on the job, and the caller re-sends with the acknowledgement once
 * the operator confirms. Any other failure is thrown for the caller's toast.
 */
export async function assignContractorRequest(
  jobPhaseId: string,
  body: JobPhaseAssignContractorDto,
): Promise<AssignContractorResult> {
  try {
    const { data } = await axiosInstance.post<JobPhaseDto>(
      `job-phase/${jobPhaseId}/assign-contractor`,
      body,
    );
    return { status: 'assigned', jobPhase: data };
  } catch (error: any) {
    if (error?.response?.status === 409) {
      const conflict = error.response.data as JobPhaseAssignConflictDto;
      return {
        status: 'previously-assigned',
        previouslyAssignedPhases: conflict.previouslyAssignedPhases ?? [],
      };
    }
    throw new Error(
      extractErrorMessage(error, 'Failed to assign the contractor'),
    );
  }
}

export const useAssignContractor = () => {
  const assign = useCallback(assignContractorRequest, []);
  return { assign };
};
